import { FloatingPortal, offset, useClick, useDismiss, useFloating, useInteractions } from '@floating-ui/react'
import { Popover } from '@headlessui/react'
import classNames from 'classnames'
import { Calendar as CalendarIcon } from 'iconoir-react'
import { compact } from 'lodash'
import moment from 'moment'
import { ReactElement, useEffect, useRef, useState } from 'react'
import Button from '../Button'
import CircularProgressBar from '../CircularProgressBar'
import { ContactNameIcon } from '../contacts/ContactNameIcon'
import { RichTextEditor } from '../editor/RichTextEditor'
import CalendarPicker from '../marketing/CalendarPicker'
import { Toggle } from '../Toggle'
import { useOutboundCall } from './OutboundCallProvider'
import { formatDate } from 'app/date.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import useFakeProgress from 'app/hooks/fakeProgress'
import type { ActivityJson } from 'app/types/entities'
import type { CallJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import { setModalTitle } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { logError } from 'lib/logging'
import { useCallStore } from 'app/stores/StoreContext'

export type MockOutboundCallEnrichmentJson = {
  id: string
  orgId: string
  workspaceId: string
  locationId: string
  callId: string
  connected: boolean
  scheduled: boolean
  appointmentDate: string
  note: string
}

// Header component that shows call status badge
export function PostCallModalHeader() {
  const { activeCall } = useOutboundCall()
  // Show "In Progress" when polling OR when activeCall is not yet set (call just initiated)
  const showInProgress = !activeCall || activeCall.status === `polling`

  return (
    <div className="flex items-center gap-x-2">
      <p>Outbound Call</p>
      {showInProgress ? (
        <span className="rounded border bg-info-minimal px-1.5 py-0.5 text-xs font-medium not-italic leading-4 text-info-strong">
          In Progress
        </span>
      ) : (
        <span className="rounded border bg-success-minimal px-1.5 py-0.5 text-xs font-medium not-italic leading-4 text-success-strong">
          Completed
        </span>
      )}
    </div>
  )
}

// Simple toggle wrapper component that matches FollowUp form styling
function SimpleToggle({ value, onChange }: { value: boolean; onChange: (val: boolean) => void }) {
  return (
    <div className="flex items-center gap-2">
      <Toggle defaultValue={value} handleToggle={onChange} size="md" />
      <span className="w-6 text-sm font-normal text-default">{value ? `Yes` : `No`}</span>
    </div>
  )
}

// Simple date picker component that doesn't require ValidatedForm context
function SimpleDatePicker({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (val: string) => void
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [referenceElement, setReferenceElement] = useState<HTMLButtonElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)

  const floatingData = useFloating({
    open: isOpen,
    placement: `bottom-start`,
    strategy: `fixed`,
    elements: { reference: referenceElement, floating: popperElement },
    onOpenChange: setIsOpen,
    middleware: [offset(5)],
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(floatingData.context),
    useDismiss(floatingData.context),
  ])

  const handleDateChange = (date: Date) => {
    onChange(moment(date).format(`YYYY-MM-DD`))
    setIsOpen(false)
  }

  const displayValue = value ? formatDate({ value: moment(value), dateOnly: true, useLocalUtc: true }) : `Select Date`

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium leading-4 text-default">{label}</label>
      <Popover className="relative">
        <Popover.Button
          ref={setReferenceElement}
          className={classNames(
            `flex w-full items-center rounded-lg border border-default bg-surface px-[17px] py-[14px] text-left shadow-elevation-1 sm:text-body-sm`,
            !value ? `text-minimal` : `text-strong`,
          )}
          {...getReferenceProps()}
          onClick={() => setIsOpen(!isOpen)}
        >
          <CalendarIcon className="h-4 w-4" />
          <div className="ml-4 flex grow items-center">
            <span>{displayValue}</span>
          </div>
        </Popover.Button>
        {isOpen && (
          <FloatingPortal>
            <Popover.Panel
              ref={setPopperElement}
              className="z-50 w-80 rounded-lg border border-default bg-surface-raised p-2 shadow-elevation-2"
              style={floatingData.floatingStyles}
              {...getFloatingProps()}
              static
            >
              <CalendarPicker
                date={value || undefined}
                onDateChange={handleDateChange}
                limitToMonth={false}
                allowPastDates={false}
                allowFutureDates={true}
              />
            </Popover.Panel>
          </FloatingPortal>
        )}
      </Popover>
    </div>
  )
}

export function PostCallModal({
  call,
  callId,
  calledNumber,
  contact,
  activity,
  onSubmit,
  onContactRefresh,
  outboundCallEnrichment,
}: {
  call?: CallJson
  callId?: string
  calledNumber?: string
  contact: ContactJson
  activity?: ActivityJson
  outboundCallEnrichment?: MockOutboundCallEnrichmentJson
  onSubmit: (activity?: ActivityJson, outboundCallEnrichmentJson?: MockOutboundCallEnrichmentJson) => void
  onContactRefresh?: () => void
}): ReactElement {
  const { activeCall } = useOutboundCall()
  const callStore = useCallStore()

  // Use activeCall.call if available (updated via polling), fallback to props
  const resolvedCall = activeCall?.call ?? call
  const resolvedCallId = callId ?? resolvedCall?.id
  const resolvedCalledNumber = calledNumber ?? resolvedCall?.callerPhone

  // Update modal title when call status changes (only for active outbound calls)
  useEffect(() => {
    if (activeCall) {
      setModalTitle(<PostCallModalHeader />)
    }
  }, [activeCall?.status])
  const [progressOpen, setProgressOpen] = useState<boolean>(false)
  const [progress, progressStart, progressEnd] = useFakeProgress()
  const [saving, setSaving] = useState(false)
  const isMountedRef = useRef(true)

  // Form state - initialize from enrichment, then activity tags, then defaults
  const [connected, setConnected] = useState<boolean>(
    outboundCallEnrichment?.connected ?? activity?.tags?.includes(`Connected`) ?? true,
  )
  const [scheduled, setScheduled] = useState<boolean>(
    outboundCallEnrichment?.scheduled ?? activity?.tags?.includes(`Appointment Scheduled`) ?? false,
  )
  const [appointmentDate, setAppointmentDate] = useState<string>(
    outboundCallEnrichment?.appointmentDate ?? resolvedCall?.appointmentDateString ?? ``,
  )
  const [note, setNote] = useState<string>(activity?.note ?? outboundCallEnrichment?.note ?? ``)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const submitEnrichment = async () => {
    setProgressOpen(true)
    progressStart()
    setSaving(true)

    // Build tags array based on form state
    const tags = compact([
      connected ? `Connected` : `Not Connected`,
      scheduled ? `Appointment Scheduled` : `Appointment Not Scheduled`,
    ])

    // Build call update payload
    const callUpdate = {
      tags,
      note: note || undefined,
      appointmentDateString: appointmentDate || undefined,
    }

    try {
      if (!resolvedCallId) throw new Error(`Call ID is required`)
      const result = await callStore.update(resolvedCallId, callUpdate)
      if (result.error) {
        logError(`Error updating call`, result.error)
        notifyError(result.error.message)
        return
      }
      if (!isMountedRef.current) return
      notifySuccess(`Call updated successfully`)
      onContactRefresh?.()
      onSubmit(
        activity ? { ...activity, tags, note } : undefined,
        outboundCallEnrichment ? { ...outboundCallEnrichment, connected, scheduled, appointmentDate, note } : undefined,
      )
    } catch (error) {
      logError(`Error updating call`, error)
      notifyError(error instanceof Error ? error.message : `Error updating call`)
    } finally {
      setSaving(false)
      progressEnd()
    }
  }

  const contactInfoHeader = (
    <div className="flex items-center gap-2 bg-surface-subtle px-6 py-3">
      <ContactNameIcon contact={contact} heightClassName="h-10" widthClassName="w-10" />
      <div className="flex h-10 flex-col justify-between">
        <p className="text-strong text-ui-sm">{contact?.name || `-`}</p>
        <p className="text-subtle text-body-xs">{formatPhoneNumber(resolvedCalledNumber)}</p>
      </div>
    </div>
  )

  if (progressOpen) {
    return (
      <div className="flex flex-col items-center gap-y-6 py-4 align-middle">
        <CircularProgressBar progress={progress} />
        <p className="px-6 text-body-sm text-default">
          Updating in progress. This may take up to 30 seconds. We appreciate your patience.
        </p>
      </div>
    )
  }

  return (
    <>
      {contactInfoHeader}
      <div className="flex w-[512px] flex-col gap-y-3.5 px-6 py-4">
        <div className="flex items-center justify-between py-2">
          <span className="text-sm font-medium text-default">Did you connect?</span>
          <SimpleToggle value={connected} onChange={setConnected} />
        </div>
        <div className="flex items-center justify-between py-2">
          <span className="text-sm font-medium text-default">Did you schedule?</span>
          <SimpleToggle value={scheduled} onChange={setScheduled} />
        </div>
        {scheduled && (
          <SimpleDatePicker label="Date of Appointment" value={appointmentDate} onChange={setAppointmentDate} />
        )}
        <div>
          <label className="mb-1.5 block text-sm font-medium leading-4 text-default">Note</label>
          <RichTextEditor namespace="post-call-note" text={note} placeholder="Add a note..." onChange={setNote} />
        </div>
      </div>
      <div className="flex w-full items-center justify-end gap-2 rounded-b-xl border-t border-default px-6 py-3">
        <Button label="Update Call Details" mode="primary" onClick={submitEnrichment} loading={saving} />
      </div>
    </>
  )
}
